import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import apiClient from "../client";

/**
 * One value of a closed list, as served by /api/reference/*.
 *
 * Three fields because they do three jobs: `id` is what a strategy carries, `name` is the stable token
 * we translate against, and `description` is the English label to fall back on when there is no
 * translation for it yet.
 */
export interface ReferenceValue {
  id: number;
  name: string;
  description: string;
}

interface ReferenceValuesResponse {
  values: ReferenceValue[];
}

type ReferenceList =
  | "asset-classes"
  | "timeframes"
  | "risk-profiles"
  | "strategy-directions"
  | "data-feeds"
  | "cancellation-reasons"
  | "strategy-request-priorities";

/**
 * These lists change when someone ships a release, not while a page is open, so they are cached hard.
 * Refetching per mount would put four requests behind every visit to the strategy list.
 */
const useReferenceList = (list: ReferenceList) =>
  useQuery({
    queryKey: ["reference", list],
    queryFn: async () =>
      (await apiClient.get<ReferenceValuesResponse>(`/reference/${list}`)).data
        .values,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const useAssetClasses = () => useReferenceList("asset-classes");
export const useTimeframes = () => useReferenceList("timeframes");
export const useRiskProfiles = () => useReferenceList("risk-profiles");
export const useStrategyDirections = () => useReferenceList("strategy-directions");
export const useCancellationReasons = () =>
  useReferenceList("cancellation-reasons");
export const useStrategyRequestPriorities = () =>
  useReferenceList("strategy-request-priorities");

/**
 * How a reference value is rendered.
 *
 * Translate against the stable `name`, and fall back to the server's English `description` when there
 * is no key for it. The fallback is the point: a value added to the shared package before anyone has
 * translated it renders as words rather than as `reference.LeveragedEtf`.
 */
export const useReferenceLabel = () => {
  const { t } = useTranslation("tradingStrategies");

  return (value: ReferenceValue): string =>
    t(`reference.${value.name}`, { defaultValue: value.description });
};

/**
 * Renders a stored id for display, given the list it belongs to.
 *
 * A dash for 0 and for anything unrecognised. 0 is Unknown, which the endpoint never offers, so a value
 * landing here means a publisher got ahead of this side — the row is wrong until the strategy is
 * re-saved in the back office, and a dash is the honest answer rather than inventing a label.
 */
export const useReferenceValueLabel = (values: ReferenceValue[] | undefined) => {
  const toLabel = useReferenceLabel();

  return (id: number | null | undefined): string => {
    if (!id) return "-";

    const match = values?.find((value) => value.id === id);

    return match ? toLabel(match) : "-";
  };
};
